/**
 * Auth API (H) — email + password login (no email verification). Cookies are
 * same-origin (relative `/api`), so the session cookie flows automatically; no
 * token handling in the client.
 */

const API = "/api";

export type Role = "owner" | "admin" | "writer" | "member";

export interface Me {
  authenticated: boolean;
  email?: string;
  is_superadmin?: boolean;
  /** False for invited / email-link-only accounts that haven't set one yet. */
  has_password?: boolean;
  memberships?: Record<string, Role>;
}

async function post(path: string, body: unknown): Promise<Record<string, unknown>> {
  const r = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => ({})) as Record<string, unknown>;
  if (!r.ok) throw new Error(String(data.error || `${path} ${r.status}`));
  return data;
}

export async function getMe(): Promise<Me> {
  // DEV-only preview of the authenticated layout without a real session:
  // `vite dev` + ?devauth=1. Dead-code-eliminated from production builds
  // (import.meta.env.DEV is false there), so it can never bypass prod auth.
  if (import.meta.env.DEV && new URLSearchParams(location.search).has("devauth")) {
    return { authenticated: true, email: "dev@local", is_superadmin: true, has_password: true, memberships: {} };
  }
  const r = await fetch(`${API}/auth/me`);
  if (r.status === 401) return { authenticated: false };
  if (!r.ok) throw new Error(`auth/me ${r.status}`);
  return r.json() as Promise<Me>;
}

/** Sign in to an existing account. Accounts are provisioned out-of-band (DB
 *  seed for the superadmin; owner-driven for members) — there is no self-signup. */
export async function login(email: string, password: string): Promise<void> {
  await post("/auth/login", { email, password });
}

/** Request a one-time sign-in link by email. Sends the current instance path so
 *  the verified link lands back here (the bare root is a featureless 404).
 *  Always resolves (server responds the same regardless of existence — no
 *  enumeration). */
export async function requestLoginLink(email: string): Promise<void> {
  await post("/auth/login-link/request", { email, redirect: window.location.pathname });
}

/** Set or update the password. `current_password` is required only when the
 *  account already has one; link-only accounts set their first without it. */
export async function setPassword(new_password: string, current_password?: string): Promise<void> {
  await post("/auth/set-password", { new_password, current_password });
}

/** @deprecated use setPassword — kept for callers that always pass a current. */
export async function changePassword(current_password: string, new_password: string): Promise<void> {
  await setPassword(new_password, current_password);
}

export async function logout(): Promise<void> {
  await fetch(`${API}/auth/logout`, { method: "POST" });
}

/** Effective role on an instance (superadmin acts as owner). */
export function roleOn(me: Me, instance: string): Role | null {
  if (me.is_superadmin) return "owner";
  return me.memberships?.[instance] ?? null;
}

/** Admin and owner manage the instance's API clients. */
export function canManageClients(me: Me, instance: string): boolean {
  const r = roleOn(me, instance);
  return r === "owner" || r === "admin";
}

export function canWrite(me: Me, instance: string): boolean {
  const r = roleOn(me, instance);
  return r === "owner" || r === "admin" || r === "writer";
}
